<script setup lang="ts">
import { onClickOutside, useThrottleFn, watchImmediate } from "@vueuse/core";
import gsap from "gsap";

const { theme } = useTheme();
const appReady = useAppReady()
const { xs } = useLayoutMode();

const menus = ref([
  { title: "Home", name: "" },
  { title: "About", name: "about" },
  { title: "Services", name: "service" },
  { title: "Our Partners", name: "ourpartners" },
  { title: "Our Work", name: "ourwork" },
  { title: "Industry Insights", name: "industry" },
  { title: "Contact", name: "concat" },
]);
const hudMenu = ref();
const isExpanded = ref(false);
let closeTimeout = null as any;
let flag = ref(true);
let hudMenuKey = ref(0)
const toggleMenu = useThrottleFn(() => {
  clearTimeout(closeTimeout);
  if(flag.value) return
  isExpanded.value = !isExpanded.value;
  const hudMenuBg = hudMenu.value.querySelector(".hud-menu-bg");
  const hudMenuContent = hudMenu.value.querySelector(".hud-menu-content");
  const hudMenuL2 = hudMenu.value.querySelectorAll(".o-hidden.menu-l2");
  const hudColorToggle = hudMenu.value.querySelector(".hud-mode-toggle-w");
  gsap.killTweensOf(hudMenuL2);
  gsap.killTweensOf(hudMenuBg);
  gsap.killTweensOf(hudMenuContent);
  gsap.killTweensOf(hudColorToggle);
  if (isExpanded.value) {
    gsap.to(hudMenuL2, { y: "0%", duration: 0 });
    gsap.to(hudColorToggle, { x: "0%", duration: 0 });

    gsap.to(hudMenuBg, {
      width: "99%",
      height: "99%",
      duration: 0.4,
    });
    gsap.to(hudMenuContent, {
      opacity: 1,
      duration: 0.8,
      ease: "cubic-bezier(.755, .05, .855, .06)",
    });
    gsap.from(hudMenuL2, {
      delay: 0.3,
      y: "-101%",
      duration: 0.3,
      stagger: 0.05,
      ease: "cubic-bezier(.755, .05, .855, .06)",
    });

    gsap.from(hudColorToggle, {
      delay: 0.4,
      x: "-130%",
      duration: 0.3,
      ease: "cubic-bezier(.755, .05, .855, .06)",
    });
  } else {
    gsap.to(hudMenuBg, {
      delay: 0.1,
      width: xs.value ? "0.5rem" : "0.71rem",
      height: xs.value ? "0.5rem" : "0.71rem",
      duration: 0.3,
      ease: "cubic-bezier(.755, .05, .855, .06)",
    });
    gsap.to(hudMenuContent, {
      opacity: 0,
      duration: 0.2,
      ease: "cubic-bezier(.755, .05, .855, .06)",
      delay: 0.2,
    });

    gsap.to(hudMenuL2, {
      y: "101%",
      duration: 0.2,
      ease: "cubic-bezier(.755, .05, .855, .06)",
    });

    gsap.to(hudColorToggle, {
      x: "130%",
      duration: 0.2,
      ease: "power4.inOut",
    });
  }
}, 700);

function onMouseOut() {
  closeTimeout = setTimeout(() => {
    if (isExpanded.value) {
      toggleMenu();
    }
  }, 2000);
}
function onMouseOver() {
  clearTimeout(closeTimeout);
}

onClickOutside(hudMenu, () => {
  if (isExpanded.value) {
    toggleMenu();
  }
});

function onJump() {
  toggleMenu();
}

function changeDarkMode() {
  theme.value = theme.value === "dark" ? "light" : "dark";
}

watch(xs, () => {
  if (!isExpanded.value) {
    const hudMenuBg = hudMenu.value.querySelector(".hud-menu-bg");
    gsap.set(hudMenuBg, {
      width: xs.value ? "0.5rem" : "0.71rem",
      height: xs.value ? "0.5rem" : "0.71rem",
    });
  }
});

let tr = null as any;
const tran = ref(false);
function cssTransition() {
  if (import.meta.client) {
    tran.value = false;
    if (tr) {
      clearTimeout(tr);
    }
    tr = setTimeout(() => {
      tran.value = true;
    }, 400);
  }
}

function hudUiAnimations() {
  const hudMenuW = hudMenu.value.querySelector(".hud-menu-w");
  const hudMenuBg = hudMenu.value.querySelector(".hud-menu-bg");
  gsap.to(hudMenuW, {
    y: "0%",
    opacity: 1,
    duration: 1.16,
    ease: "power1.out",
  });
  gsap.to(hudMenuBg, {
    y: "0%",
    opacity: 1,
    duration: 1,
    ease: "power1.out",
  });
}

onMounted(() => {
  watchImmediate(appReady, () => {
    if (!appReady.value)
      return;

    hudMenuKey.value = new Date().getTime()
    nextTick(() => {
      hudUiAnimations()
    })
  })

  window.addEventListener("resize", cssTransition);
  cssTransition();
  setTimeout(() => {
    flag.value = false
  }, 1000);

});

onBeforeUnmount(() => {
  window.removeEventListener("resize", cssTransition);
});
</script>

<template>
  <div ref="hudMenu" :key="hudMenuKey" class="hud-menu">
    <div
      class="hud-menu-o"
      :class="{ 'is-open': isExpanded }"
      @mouseenter="onMouseOver"
      @mouseleave="onMouseOut"
    >
      <div class="hud-menu-content">
        <ul class="hud-menu-link-w">
          <li
            v-for="item in menus"
            :key="item.name"
            class="o-hidden item"
            @click="onJump()"
          >
            <div class="o-hidden menu-l2">
              <div class="hud-menu-link">
                <div class="o-hidden page-link-inner">
                  <div class="split-text text-small btn-txt">
                    <TextView
                      :key="$route.path"
                      pos="left"
                      :isMenu="true"
                      :path="
                        item.name.startsWith('/') ? item.name : `/${item.name}`
                      "
                    >
                      <div class="btn-text">
                        {{ item.title }}
                      </div>
                    </TextView>
                  </div>
                  <div class="btn-icon-w">
                    <div class="jt" v-if="theme === 'dark'">
                      <img src="@/assets/images/icon_jt_dark.webp" alt="" />
                      <img src="@/assets/images/icon_jt_dark.webp" alt="" />
                    </div>
                    <div class="jt" v-else>
                      <img src="@/assets/images/icon_jt_ligth.webp" alt="" />
                      <img src="@/assets/images/icon_jt_ligth.webp" alt="" />
                    </div>
                  </div>
                </div>
                <div class="link-track">
                  <div class="link-track-fill" />
                </div>
              </div>
            </div>
          </li>
        </ul>
        <div class="hud-mode-o-hidden">
          <div class="hud-mode-toggle-w" @click="changeDarkMode">
            <div class="mode-toggle-track">
              <div class="mode-toggle-inner">
                <div
                  class="mode-toggle-btn"
                  :class="{ light: theme === 'light' }"
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="hud-menu-w" @click="toggleMenu">
        <div class="hud-menu-c" :class="{ tran }">
          <div class="hud-menu-line is-1" />
          <div class="hud-menu-line is-2" />
          <div class="hud-menu-line is-3" />
        </div>
      </div>
      <div class="hud-menu-bg" />
    </div>
  </div>
</template>

<style lang="scss" scoped>
.hud-menu {
  width: 100%;
  max-width: 1920px;
  left: 50%;
  position: fixed;
  transform: translateX(-50%);
  bottom: 32px;
  display: flex;
  z-index: 2;
  justify-content: flex-end;
  padding-right: 210px;
  pointer-events: none;
  .hud-menu-o {
    width: 306px;
    height: 440px;
    padding-top: 2px;
    overflow: hidden;
    position: relative;
    pointer-events: none;
    padding: 21px 36px 0 36px;
    .hud-menu-content {
      opacity: 0;
      .hud-menu-link-w {
        .o-hidden {
          overflow: hidden;
          &.item {
            height: 50px;
            .menu-l2 {
              height: 50px;
              .hud-menu-link {
                font-size: 20px;
                color: var(--muneFontColor);
                height: 50px;
                position: relative;
                display: flex;
                align-items: center;
                padding: 0 14px 0 13px;
                justify-content: space-between;
                font-weight: 300;
                cursor: pointer;
                .page-link-inner {
                  width: 100%;
                  display: flex;
                  align-items: center;
                  justify-content: space-between;
                  .btn-text {
                    text-shadow: 0px 1em 0px var(--muneFontColor);
                    line-height: 1;
                  }
                  .btn-icon-w {
                    width: 23px;
                    height: 23px;
                    overflow: hidden;
                    pointer-events: none;
                    .jt {
                      width: 55px;
                      height: 23px;
                      display: flex;
                      align-items: center;
                      justify-content: space-between;
                      position: relative;
                      left: -33px;
                      transition: 1.2s cubic-bezier(0.165, 0.84, 0.44, 1);
                      img {
                        width: 23px;
                        height: 23px;
                      }
                    }
                  }
                }
                .link-track {
                  position: absolute;
                  left: 0;
                  bottom: 1px;
                  background: var(--muneLineColor);
                  width: 100%;
                  height: 1px;
                  .link-track-fill {
                    background: var(--muneFontColor);
                    width: 100%;
                    transition: transform 0.6s;
                    height: 1px;
                    transform: scale(0);
                    backface-visibility: hidden;
                    transform-origin: 0%;
                    transform-style: preserve-3d;
                  }
                }
              }
            }
            &:hover {
              .menu-l2 {
                .hud-menu-link {
                  .link-track-fill {
                    transform: scale3d(1, 1, 1);
                  }
                  .btn-icon-w {
                    .jt {
                      transform: translateX(33px);
                    }
                  }
                }
              }
            }
            &:last-child {
              .link-track {
                display: none;
              }
            }
            &:nth-child(6){
              .menu-l2 {
                .hud-menu-link {
                  .page-link-inner {
                    .btn-text {
                      line-height: 1.12;
                    }
                  }
                }
              }
            }
          }
        }
      }

      .hud-mode-o-hidden {
        width: 64px;
        height: 24px;
        overflow: hidden;
        position: relative;
        top: 24px;
        cursor: pointer;
        .hud-mode-toggle-w {
          width: 64px;
          height: 24px;
          .mode-toggle-track {
            width: 64px;
            height: 24px;
            .mode-toggle-inner {
              width: 64px;
              height: 24px;
              border: 1PX solid var(--muneBtnLineColor);
              border-radius: 4px;
              display: flex;
              align-items: center;
              padding: 0 5px;
              .mode-toggle-btn {
                width: 20px;
                height: 16px;
                border-radius: 4px;
                background-color: var(--muneFontColor);
                transition: transform 0.4s
                  cubic-bezier(0.68, -0.55, 0.265, 1.55);
                &.light {
                  transform: translateX(32px);
                }
              }
            }
          }
        }
      }
    }

    .hud-menu-w {
      position: absolute;
      right: 1px;
      bottom: 1px;
      width: 71px;
      height: 71px;
      pointer-events: all;
      opacity: 0;
      transform: translateY(101%);
      .hud-menu-c {
        width: 71px;
        height: 71px;
        position: relative;
        justify-content: center;
        align-items: center;
        display: flex;
        cursor: pointer;
        &.tran {
          .hud-menu-line {
            transition: transform 0.4s cubic-bezier(0.68, -0.55, 0.265, 1.55);
          }
        }
        .hud-menu-line {
          width: 38px;
          height: 1PX;
          position: absolute;
          background-color: var(--muneCloseLineColor);
          will-change:transform;
          &.is-1 {
            transform: translate(0) translateY(-14px);
          }
          &.is-2 {
            transform: translate(0);
          }
          &.is-3 {
            transform: translateY(14px);
          }
        }
        &:hover {
          .hud-menu-line {
            &.is-1 {
              transform: translate(0, -16px);
            }
            &.is-2 {
              transform: translate(0, 0);
            }
            &.is-3 {
              transform: translate(0, 16px);
            }
          } 
        }
      }
      &:hover{
       &+.hud-menu-bg {
            background-color: var(--muneBgColor);
            border: 1px solid transparent;
        }
      }
    }

    .hud-menu-bg {
      width: 71px;
      height: 71px;
      position: absolute;
      will-change: transform;
      right: 1px;
      bottom: 1px;
      z-index: -1;
      border-radius: 13px;
      opacity: 0;
      transform: translateY(101%);
      border: 1px solid var(--muneCloseLineColor); 
      transition: background-color 0.4s,border-color 0.4s;
    }

    &.is-open {
      pointer-events: all;
      .hud-menu-w {
        .hud-menu-c {
          .hud-menu-line {
            &.is-1 {
              transform: translate(0, 0) rotate(45deg);
            }
            &.is-2 {
              transform: translate(0, 0) scale3d(0, 1, 1);
            }
            &.is-3 {
              transform: translate(0, 0) rotate(-45deg);
            }
          }
          &:hover {
            .hud-menu-line {
              &.is-1 {
                transform: translate(0, 0) rotate(35deg);
              }
              &.is-2 {
                transform: translate(0, 0) scale3d(0, 1, 1);
              }
              &.is-3 {
                transform: translate(0, 0) rotate(-35deg);
              }
            }
          }
        }
      }
      .hud-menu-bg{
        border: 1px solid var(--muneBorderColor)!important; 
        // border: 1px solid var(--muneCloseLineColor)!important; 
        background-color: var(--muneBgColor);
      }
    }
  }
}
#app {
  &.xs {
    .hud-menu {
      width: 100%;
      padding-right: 20px;
      .hud-menu-o {
        width: 216px;
        height: 321px;
        padding:10px 25px 0 25px;
        .hud-menu-content {
          .hud-menu-link-w {
            .o-hidden.item {
              height: 37px;
              .menu-l2 {
                height: 37px;
                .hud-menu-link {
                  font-size: 14px;
                  height: 37px;
                  padding-right: 0;
                  .page-link-inner {
                    .btn-icon-w {
                      .jt {
                        width: 55px;
                        img {
                          width: 12px;
                          height: 12px;
                        }
                      }
                    }
                  }
                }
              }
            &:hover {
              .menu-l2 {
                .hud-menu-link {
                  .btn-icon-w {
                    .jt {
                      transform: translateX(43px);
                    }
                  }
                }
              }
            }
            }
          }

          .hud-mode-o-hidden {
            width: 45px;
            top: 19px;
            cursor: pointer;
            .hud-mode-toggle-w {
              width: 45px;
              height: 16px;
              .mode-toggle-track {
                width: 45px;
                height: 16px;
                .mode-toggle-inner {
                  width: 45px;
                  height: 16px;
                  border-radius: 3px;
                  padding: 0 3px;
                  .mode-toggle-btn {
                    width: 14px;
                    height: 11px;
                    border-radius: 3px;
                    &.light {
                      transform: translateX(23px);
                    }
                  }
                }
              }
            }
          }
        }

        .hud-menu-w {
          width: 50px;
          height: 50px;
          .hud-menu-c {
            width: 50px;
            height: 50px;
            .hud-menu-line {
              width: 25px;
              height: 1PX;
              will-change: transform;
              &.is-1 {
                transform: translate(0) translateY(-9px);
              }
              &.is-3 {
                transform: translate(0) translateY(9px);
              }
            }
          }
        }
        .hud-menu-bg {
          width: 50px;
          height: 50px;
          border-radius: 10px;
        }
        &.is-open {
          .hud-menu-w {
            .hud-menu-c {
              .hud-menu-line {
                &.is-1 {
                  transform: translate(0, 0) rotate(45deg);
                }
                &.is-2 {
                  transform: translate(0, 0) scale3d(0, 1, 1);
                }
                &.is-3 {
                  transform: translate(0, 0) rotate(-45deg);
                }
              }
            }
          }
        }
      }
    }
  }
}
</style>
